import { Availability, getAvailabilityLabel } from "@caredna-marlin/utils/client/availability"
import { timeLabel, timezoneAbbreviation } from "@caredna-marlin/utils/dates"
import { Badge, Button, ListSubheader, Menu, MenuItem, Tooltip } from "@mui/material"
import { startOfToday, subMilliseconds } from "date-fns"
import { getTimezoneOffset } from "date-fns-tz"
import { capitalize, groupBy, keyBy, mapValues } from "lodash"
import { MouseEvent, useState } from "react"
import Help from "~/components/common/Help"
import { AvailabilityByTimespanQuery, AvailTzHandling, useAvailabilityByTimespanQuery } from "~/graphql/__generated__/graphql"
import { AvailabilityAction, MulipleAvailabilityAction } from "~/hooks/useAvailabilityOperation"

type Props = {
  action: AvailabilityAction | MulipleAvailabilityAction
  availability: Availability
  additionalProviders: Record<string, boolean>
  loading?: boolean
  disabled?: boolean
  onSave: (tzHandling: AvailTzHandling, providerTzs: Record<string, string>) => void
}

const actionLabel = (action: Props["action"]) => action === "template" ? "Apply template" : capitalize(action)

const SaveWithTimezoneHandling = ({ action, additionalProviders, availability, loading, disabled, onSave }: Props) => {
  const [tzMenuOpen, setTzMenuOpen] = useState<null | HTMLElement>(null);

  const { data } = useAvailabilityByTimespanQuery({ variables: {
    start: startOfToday().toISOString(),
  }});

  const provider = data?.provider.find(p => p.id === availability?.providerId);

  const providerTzs = mapValues(keyBy(data?.provider || [], "id"), p => p.addressTimezone || "")
  const timezones = groupBy(data?.provider.filter(p => additionalProviders[p.id] || p.id === availability.providerId) || [], "addressTimezone")
  const multiTimezone = Object.keys(timezones).length > 1

  const providerNames = mapValues(keyBy(data?.provider || [], "id"), p => `${p.firstName} ${p.lastName}`)

  const onSaveClick = async (e: MouseEvent<HTMLButtonElement>) => {
    if (action !== "delete" && multiTimezone) {
      setTzMenuOpen(e.currentTarget)
      return
    }

    onSave(AvailTzHandling.UseProviderTZs, providerTzs)
  }

  const includePrimaryInList = action === "create" || action === "edit" || action === "delete"
  const providersSelected = Object.keys(additionalProviders).filter(k => additionalProviders[k])
  if (includePrimaryInList) {
    providersSelected.unshift(availability.providerId.toString())
  }

  return <>
    <Button loading={loading} disabled={disabled} onClick={onSaveClick} color="primary" sx={{ pr: providersSelected.length ? 2 : undefined }}>
      {actionLabel(action)}
      <MultiProviderBadge action={action} providerNames={providerNames} providersSelected={providersSelected} />
    </Button>
    {multiTimezone && <Menu open={!!tzMenuOpen} onClose={() => setTzMenuOpen(null)} anchorEl={tzMenuOpen}>
      <ListSubheader>
        {capitalize(action)} {getAvailabilityLabel(availability)}
        <Help
          slotProps={{ tooltip: { sx: { maxWidth: 400 }}}}
          text={<TimezoneTooltip
            additionalProviders={additionalProviders}
            availability={availability}
            providerTimezone={provider?.addressTimezone || ""}
            timezones={timezones}
          />}
        />
      </ListSubheader>
      <MenuItem>
        <Button disabled={disabled || loading} onClick={() => onSave(AvailTzHandling.UseProviderTZs, providerTzs)}>
          Same local hours for each provider
        </Button>
      </MenuItem>
      <MenuItem>
        <Button disabled={disabled || loading} onClick={() => onSave(AvailTzHandling.UseCalendarTime, providerTzs)}>
          Same exact time for everyone
        </Button>
      </MenuItem>
    </Menu>}
  </>
}

type TooltipProps = {
  additionalProviders: Record<string, boolean>
  availability: Availability
  providerTimezone: string
  timezones: Record<string, AvailabilityByTimespanQuery["provider"]>
}
const TimezoneTooltip = ({
  additionalProviders,
  availability: { localStartTime, localEndTime },
  providerTimezone,
  timezones,
}: TooltipProps) => {
  const otherExampleTz = Object.keys(timezones).filter(tz => tz !== providerTimezone)[0];

  const fromOffset = getTimezoneOffset(providerTimezone)
  const toOffset = getTimezoneOffset(otherExampleTz)

  const startOther = subMilliseconds(localStartTime, fromOffset - toOffset)
  const endOther = subMilliseconds(localEndTime, fromOffset - toOffset)

  const startEnd = (start: Date, end: Date, tz: string) => `${timeLabel(start)} - ${timeLabel(end)} ${timezoneAbbreviation(tz)}`

  return <div>
    Selected providers are in different timezones. Select how it should be handled.

    <p><b>Same local hours</b> copies the same working hours for each provider</p>
    <ul>
      <li>{startEnd(localStartTime, localEndTime, providerTimezone)} for {timezones[providerTimezone][0].firstName}</li>
      <li>{startEnd(localStartTime, localEndTime, otherExampleTz)} for {timezones[otherExampleTz][0].firstName}</li>
      {Object.values(additionalProviders).filter(Boolean).length > 1 && <li>...etc...</li>}
    </ul>

    <b>Same exact time</b> copies the same fixed time, which may appear different locally.

    <ul>
      <li>{startEnd(localStartTime, localEndTime, providerTimezone)} for {timezones[providerTimezone][0].firstName}</li>
      <li>{startEnd(startOther, endOther, otherExampleTz)} for {timezones[otherExampleTz][0].firstName}</li>
      {Object.values(additionalProviders).filter(Boolean).length > 1 && <li>...etc...</li>}
    </ul>
  </div>
}

type BadgeProps = {
  action: Props["action"]
  providersSelected: string[]
  providerNames: Record<string, string>
}

const MultiProviderBadge = ({ action, providerNames, providersSelected }: BadgeProps) => {
  return <Tooltip title={<div style={{ padding: 4 }}>
    {actionLabel(action)} for {providersSelected.length} providers:
    <ul style={{ marginTop: 4, marginBottom: 0 }}>
      {providersSelected.slice(0, 4).map(providerId => <li key={providerId}>{providerNames[providerId]}</li>)}
    </ul>
    {providersSelected.length > 4 && <span style={{paddingLeft: 24}}>...{providersSelected.length - 4} more</span>}
  </div>}>
      <Badge badgeContent={providersSelected.length > 1 ? ("x" + providersSelected.length) : undefined} color="primary" sx={{ top: -12, pl: 1.5, textTransform: "none" }} />
  </Tooltip>
}

export default SaveWithTimezoneHandling
